import PageLayout from "@/components/custom/PageLayout";
import DriverCheckInBg from "@/assets/backgrounds/DriverCheckInBg.svg";
import i18n from "@/lang/i18n";
import LoginTokenForm from "./LoginTokenForm";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorMessageFullCard from "@/components/custom/ErrorMessageFullCard";
import { fetchLoginTokenDetails, resetLoginReducerState } from "@/redux/loginDetailsSlice";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@/redux/store";
import LoginTokenCarDetailsCard from "@/components/custom/LoginTokenCarDetailsCard";
import { Button } from "@/components/ui/button";
import CheckInViaDriverForm from "@/components/custom/CheckInViaDriverForm";
import { setTyreBrands } from "@/redux/checkInViaDriverDetailsSlice";
import CheckInDriverService from "@/services/api/CheckInDriverService";
import { CheckInViaDriverResponse } from "@/central-atoms/everest-fleet-pwa/responses";
import { useDriverSelfCheckIn, useSelfCheckInDetails } from "@/hooks";
import LoginTokenCarDetailsSelfCheckIn from "@/components/custom/LoginTokenCarDetailsSelfCheckIn";
import { setSelfCheckInDetails } from "@/redux/selfCheckInDetailsSlice";
import { FeatureFlagNames } from "@/central-atoms/feature-flags/enums";
import { canViewFeature } from "@/utils/appInitializer";

export const CheckInViaDriverFlow = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const loginDetails = useSelector((state: RootState) => state.loginDetails.details);
  const loginDetailsStatus = useSelector((state: RootState) => state.loginDetails.status);
  const tyreBrands = useSelector((state: RootState) => state.checkInViaDriverDetails.tyreBrands);
  const { odo: ocrOdo, soc: ocrSoc } = useSelfCheckInDetails();

  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const [token, setToken] = useState("");
  const selfCheckIn = useDriverSelfCheckIn();

  const handleGoBackFromLoginDetailsError = () => {
    dispatch(resetLoginReducerState());
    navigate('/driver-home');
  }

  useEffect(() => {
    if (loginDetailsStatus === 'failed') {

      if (loginDetails?.data?.messages) {
        setErrorMessage(loginDetails.data.messages)
      }
    }
  }, [loginDetailsStatus])

  const handleConfirm = (_loginToken: number) => {
    setToken(`${_loginToken}`);
    dispatch(fetchLoginTokenDetails({token: `${_loginToken}`}));
  }

  const fetchTyreBrands = async () => {
    try {
      const response = await CheckInDriverService.getTyreBrands();

      // Dispatch actions to store user and token in Redux
      dispatch(setTyreBrands(response));
    } catch (err) {
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
  
    fetchTyreBrands();
    /* 
      Abort the request as it isn't needed anymore, the component being 
      unmounted. It helps avoid, among other things, the well-known "can't
      perform a React state update on an unmounted component" warning.
    */
    return () => abortController.abort();
  }, []);

  const canViewOCR = canViewFeature(FeatureFlagNames.everest_check_in_ocr);

  const handleCreateCheckInToken = async (data: {
    left_front_tyre_brand: string;
    left_front_tyre_number: string;
    right_front_tyre_brand: string;
    right_front_tyre_number: string;
    left_rear_tyre_brand: string;
    left_rear_tyre_number: string;
    right_rear_tyre_brand: string;
    right_rear_tyre_number: string;
    stepney_tyre_brand: string;
    stepney_tyre_number: string;
  }) => {
    try {
      await CheckInDriverService.checkInViaDriver({
        ...data,
        token: token,
        edited_odometer_reading: canViewOCR ? (ocrOdo || 0  ) : (loginDetails?.data.details.odo || 0),
        edited_soc: canViewOCR ? (ocrSoc || 0) : (loginDetails?.data.details.soc || 0),
      });

      if (selfCheckIn) {
        navigate('/driver-check-in-success');
      }
      else {
        navigate('/driver-home');
      }
    }
    catch(err: any) {
      if (err?.response?.data) {
        const errorResponse = err.response.data as CheckInViaDriverResponse;
        setErrorMessage(errorResponse.data.messages);
      }
    }
  }

  const handleOCRComplete = (odo: number, soc: number) => {
    dispatch(setSelfCheckInDetails({ odo, soc }));
  };

  return (
    <PageLayout noPad>
      <div className="h-full relative">
        {
          errorMessage.length > 0 &&
            <ErrorMessageFullCard errorString={errorMessage} handleBackToHome={handleGoBackFromLoginDetailsError} />
        }
        {
          loginDetailsStatus === 'succeeded' ? 
            (canViewOCR && ocrOdo === null && ocrSoc === null) ?
            <div className="w-full h-full bg-primary">
              <div className="absolute bottom-0 w-full rounded-t-[30px] h-[80vh] bg-white">
              </div>
              <LoginTokenCarDetailsSelfCheckIn 
                  vrn={loginDetails?.data.details.vrn || ''} 
                  onOCRComplete={handleOCRComplete}
              />
            </div>
            :
            <div className="w-full h-full bg-primary">
              <div className="absolute bottom-0 w-full rounded-t-[30px] h-[80vh] bg-white">
              </div>
              <div className="px-[26px] relative z-10 pt-[26px]">
                <LoginTokenCarDetailsCard 
                  vrn={loginDetails?.data.details.vrn || ''} 
                  odo={canViewOCR ? (ocrOdo  || 0) : (loginDetails?.data.details.odo || 0)} 
                  soc={canViewOCR ? (ocrSoc || 0) : (loginDetails?.data.details.soc || 0)} 
                />
                {
                  tyreBrands &&
                    <CheckInViaDriverForm tyreBrands={tyreBrands} handleSubmit={handleCreateCheckInToken} />
                }
              </div>
            </div>
          :
            <>
              <div className="absolute w-full top-[0px] bg-primary">
                <img
                  src={DriverCheckInBg}
                  alt="Background"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex items-center justify-center relative z-10 w-full h-full">
                <div className="w-full px-[41px]">
                  <p className="text-white font-semibold text-[24px] text-center mb-[51px]">{i18n.t('driver_check_in')}</p>
                  <LoginTokenForm handleConfirm={handleConfirm} />
                </div>
              </div>
            </>
        }
      </div>
    </PageLayout>
  );
};